import type { Schemas } from '@shopware/api-client/api-types';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import { useAuthCookie } from '~/composables/useAuthCookie';
import { useIframeAuthFlow } from '~/composables/useIframeAuthFlow';
import { COOKIE_NAME_DOCEBO_ACCESS_TOKEN } from '~/types/cookieNames';

interface LoginOptions {
    method?: 'doccheck';
    redirectTo?: string;
}

interface LoginStateOptions {
    includeShopware?: boolean;
}

/**
 * A user in our application currently has two sessions: Keycloak and Shopware.
 * This composable aims to provide unified utilities to manage the login state.
 */
export const useLoginState = (routeArg?: RouteLocationNormalizedGeneric, options: LoginStateOptions = {}) => {
    const shopwareUser = options.includeShopware === false ? null : useShopwareUser();
    const userShopware = shopwareUser?.user ?? ref(null);
    const isLoggedInShopware = shopwareUser?.isLoggedIn ?? computed(() => false);
    const logoutShopware = shopwareUser?.logout ?? (() => Promise.resolve());
    const auth = useAuth();
    const { urlSegment } = useLanguage(routeArg);
    const route = routeArg?.fullPath ? routeArg : useRoute();
    const { runAll } = usePreLoginHooks();
    const { shouldUseIframeAuthFlow } = useIframeAuthFlow();

    const isKeycloakAuthenticated = computed(() => Boolean(auth.value));
    const isLoggedIn = computed(() => isKeycloakAuthenticated.value && isLoggedInShopware.value);
    const displayFirstName = computed(
        () =>
            auth.value?.given_name ||
            auth.value?.nickname ||
            auth.value?.name ||
            auth.value?.address?.firstName ||
            userShopware.value?.firstName ||
            ''
    );

    const doceboAccessTokenCookie = useAuthCookie(COOKIE_NAME_DOCEBO_ACCESS_TOKEN);
    const isDoceboAuthenticated = computed(() => Boolean(doceboAccessTokenCookie.value));

    const resolveLoginRedirectTo = (options?: LoginOptions) => {
        if (options?.redirectTo) {
            return options.redirectTo;
        }

        return route.fullPath;
    };

    const login = async (options?: LoginOptions) => {
        await runAll();

        const redirectTo = resolveLoginRedirectTo(options);

        if (shouldUseIframeAuthFlow.value) {
            const { useAuthPopup } = await import('~/composables/useAuthPopup');
            const { loginWithPopup } = useAuthPopup();
            await loginWithPopup(redirectTo, { method: options?.method });
            return;
        }

        const urlSearchParams = new URLSearchParams();
        urlSearchParams.set('redirect_to', redirectTo);
        if (options?.method === 'doccheck') urlSearchParams.set('method', 'doccheck');
        const url = `/api/auth/login?${urlSearchParams.toString()}`;
        log.debug(`[useLoginState] Initiating login, redirecting to: ${url}`);
        await navigateTo(url, {
            external: true
        });
    };

    const logout = async () => {
        /**
         * Remove shoe from cart
         * TODO: Remove with MP-1234
         */
        const { cart, removeItemById } = useCart();

        let shoeId: string | null = null;
        cart.value?.lineItems?.forEach((element) => {
            if (element.payload?.productNumber?.includes('MER0006')) {
                shoeId = element.id;
            }
        });
        if (shoeId) await removeItemById(shoeId);

        /**
         * Store current cart items in session storage to restore them after logout
         */
        const itemsData = cart.value?.lineItems?.map((lineItem: Schemas['LineItem']) => ({
            id: lineItem.id,
            referencedId: lineItem.referencedId,
            quantity: lineItem.quantity,
            type: lineItem.type
        }));

        // `logoutShopware()` throws an error if you try to logout but you aren't logged in currently
        try {
            await logoutShopware();
        } catch (error) {
            console.warn('logoutShopware() failed', error);
        }

        const { addProducts } = useCart();
        await addProducts(itemsData);
        sessionStorage.setItem('shopware-preLogoutCard', JSON.stringify(itemsData));

        /**
         * Redirect to homepage if we are in the My Account section
         */
        let redirectUrl = route.fullPath;
        if (route.meta.isAccountRoute === true) {
            redirectUrl = `/${urlSegment.value}`;
        }

        /**
         * Logout in Storyblok iframe within popup.
         */
        if (shouldUseIframeAuthFlow.value) {
            const { useAuthPopup } = await import('~/composables/useAuthPopup');
            const { logoutWithIframeFlow } = useAuthPopup();
            await logoutWithIframeFlow(redirectUrl);
            return;
        }

        /**
         * Redirect to logout endpoint
         */
        const searchParams = new URLSearchParams();
        searchParams.set('redirect_to', redirectUrl);
        const url = `/api/auth/logout?${searchParams.toString()}`;
        log.debug(`[useLoginState] Logging out, redirecting to: ${url}`);
        await navigateTo(url, {
            external: true
        });
    };

    return {
        login,
        logout,
        /** Use this only if Shopware is actually required for your use case. Use `isKeycloakAuthenticated` otherwise. */
        isLoggedIn,
        /** Prefer this over `isLoggedIn` and `isDoceboAuthenticated` if no Shopware, respectively Docebo, is required for your use case. */
        isKeycloakAuthenticated,
        displayFirstName,
        userShopware,
        auth,
        /** Use this only if Docebo is actually required for your use case. Use `isKeycloakAuthenticated` otherwise. */
        isDoceboAuthenticated,
        doceboAccessTokenCookie
    };
};
